import { useEffect } from 'react';

/**
 * Lightweight page rendered inside the OAuth popup.
 *
 * After the user consents at the OAuth provider (Microsoft / GitHub / Google),
 * the provider redirects here with `?code=...&state=...`.
 * We forward those to the opener window via `postMessage` and close.
 *
 * NOTE: targetOrigin is '*' because Google rejects .local redirect URIs,
 * so in dev the popup lands on http://localhost while the opener is on
 * https://eu-w1.atlas.local (different origin). Security relies on the
 * one-time code + state CSRF token, not on origin filtering.
 */
export function OAuthCallbackPage() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const error = params.get('error');
    const adminConsent = params.get('admin_consent');
    const tenant = params.get('tenant');

    if (window.opener) {
      if (adminConsent) {
        window.opener.postMessage(
          {
            type: 'admin-consent-callback',
            admin_consent: adminConsent,
            tenant: tenant ?? undefined,
            error: error ?? undefined,
          },
          '*',
        );
      } else if (error) {
        window.opener.postMessage(
          {
            type: 'oauth-callback',
            error,
            reason: 'provider_error',
          },
          '*',
        );
      } else if (!code || !state) {
        // ISS-853: no reenviar un callback incompleto (JSON.stringify omite
        // `undefined` y la API responde 422). Se emite un error tipado para
        // que el opener muestre un mensaje accionable sin llamar al backend.
        window.opener.postMessage(
          {
            type: 'oauth-callback',
            error: !code ? 'missing_code' : 'missing_state',
            reason: !code ? 'missing_code' : 'missing_state',
          },
          '*',
        );
      } else {
        window.opener.postMessage(
          {
            type: 'oauth-callback',
            code,
            state,
          },
          '*',
        );
      }
    } else if (adminConsent) {
      const target = new URL('/settings/integrations', window.location.origin);
      target.searchParams.set('admin_consent', adminConsent);
      if (tenant) {
        target.searchParams.set('tenant', tenant);
      }
      if (error) {
        target.searchParams.set('error', error);
      }
      window.location.replace(target.toString());
      return;
    }

    // Give the message time to deliver, then close
    setTimeout(() => window.close(), 300);
  }, []);

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4" />
        <p className="text-gray-500 dark:text-gray-400">Completing authentication...</p>
      </div>
    </div>
  );
}
